import React, { useEffect, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '~/contexts';
import { Icon } from '~/components';
import { colors, weights } from '~/styles';
import humanize from '../utils/humanize';
import { BackLink, EmptyState, Mono, Table, Td, Th } from '../ai/replay/shared';
import WorkspaceDisplay from '../ai/replay/WorkspaceDisplay';
import MemberDisplay from '../ai/replay/MemberDisplay';
import ActorCell from '../traces/ActorCell';

const HeaderTable = styled(Table)`
  margin-bottom: 1.5rem;
`;

const CHANNEL_COLORS = {
  email: colors.primary,
  slack: colors.accent,
};

const ChannelBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  text-transform: uppercase;
  color: ${({ $channel }) => CHANNEL_COLORS[$channel] || colors.grey40};
`;

const StatusBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  text-transform: uppercase;
  color: ${({ $delivered }) => ($delivered ? colors.success : colors.danger)};
  background: ${({ $delivered }) => ($delivered ? colors.success : colors.danger) + '15'};
  padding: 0.125rem 0.5rem;
  border-radius: 0.125rem;
`;

const Recipient = styled.span`
  font-size: 0.875rem;
  color: ${colors.black};
`;

const SectionTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  color: ${colors.black};
  margin: 1.5rem 0 0.75rem;
`;

const Card = styled.div`
  border: solid 1px ${colors.grey10};
  border-radius: 0.375rem;
  overflow: hidden;
`;

const FieldTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const FieldTh = styled.th`
  text-align: left;
  padding: 0.5rem 0.75rem;
  width: 9rem;
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  color: ${colors.grey55};
  text-transform: uppercase;
  vertical-align: top;
  border-bottom: solid 1px ${colors.grey10};
`;

const FieldTd = styled.td`
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  color: ${colors.black};
  word-break: break-word;
  border-bottom: solid 1px ${colors.grey10};
`;

const Body = styled.pre`
  margin: 0;
  padding: 0.75rem;
  font-family: inherit;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${colors.black};
  background: ${colors.grey5};
  white-space: pre-wrap;
  word-break: break-word;
`;

const TraceLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: ${weights.medium};
  color: ${colors.primary};

  &:hover {
    color: ${colors.accent};
  }
`;

const TraceSource = styled.span`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.8125rem;
  color: ${colors.black};
`;

const TraceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

// The content metadata shown above the message body — body/text render separately.
const BODY_FIELDS = new Set(['body', 'text']);

export default function NotificationDetail() {
  const { notificationId } = useParams();
  const api = useApi();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchNotification = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data: result } = await api.admin.notificationReplay.getNotification(notificationId);
        if (!cancelled) setData(result);
      } catch (error) {
        if (!cancelled && error.status !== 404) throw error;
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchNotification();
    return () => {
      cancelled = true;
    };
  }, [api, notificationId]);

  const notification = data?.notification;
  const content = notification?.content ?? null;
  const fields = content
    ? Object.entries(content).filter(([key, value]) => !BODY_FIELDS.has(key) && value != null && value !== '')
    : [];
  const body = content?.body ?? content?.text ?? null;

  return (
    <>
      {isLoading && <EmptyState>Loading notification...</EmptyState>}

      {!isLoading && !notification && <EmptyState>Notification not found.</EmptyState>}

      {!isLoading && notification && (
        <>
          {/* Return to the list with its filters intact — NotificationList stashes
              its query string on every change so the back link restores it. */}
          <BackLink as={Link} to={`/admin/notifications${sessionStorage.getItem('admin_notifications_search') ?? ''}`}>
            <Icon icon="arrow-left" />
            Back to notifications
          </BackLink>

          <HeaderTable>
            <thead>
              <tr>
                <Th>Channel</Th>
                <Th>Recipient</Th>
                <Th>Workspace</Th>
                <Th>Triggered by</Th>
                <Th>Status</Th>
                <Th>Sent</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td>
                  <ChannelBadge $channel={notification.channel}>{notification.channel}</ChannelBadge>
                </Td>
                <Td>
                  {notification.recipientMember ? (
                    <MemberDisplay member={notification.recipientMember} />
                  ) : (
                    <Recipient>{notification.recipient || '-'}</Recipient>
                  )}
                </Td>
                <Td>
                  <WorkspaceDisplay workspace={notification.workspace} />
                </Td>
                <Td>
                  <ActorCell actor={notification.actor} member={notification.member} />
                </Td>
                <Td>
                  <StatusBadge $delivered={notification.delivered}>
                    {notification.delivered ? 'delivered' : 'failed'}
                  </StatusBadge>
                </Td>
                <Td title={notification.occurredAt}>{moment(notification.occurredAt).format('MMM D, YYYY h:mm a')}</Td>
              </tr>
            </tbody>
          </HeaderTable>

          {data.trace?.id && (
            <>
              <SectionTitle>Trace</SectionTitle>
              <TraceRow>
                <TraceSource>{data.trace.source || '-'}</TraceSource>
                <TraceLink to={`/admin/traces/${data.trace.id}`}>
                  View trace
                  <Icon icon="arrow-right" />
                </TraceLink>
                <Mono>{data.trace.id}</Mono>
              </TraceRow>
            </>
          )}

          <SectionTitle>Content</SectionTitle>
          <Card>
            {fields.length > 0 && (
              <FieldTable>
                <tbody>
                  {fields.map(([key, value]) => (
                    <tr key={key}>
                      <FieldTh>{humanize(key)}</FieldTh>
                      <FieldTd>{String(value)}</FieldTd>
                    </tr>
                  ))}
                </tbody>
              </FieldTable>
            )}
            {body && <Body>{body}</Body>}
          </Card>
        </>
      )}
    </>
  );
}
